import { useId, useMemo, useState, type ReactNode } from "react";
import estilos from "./Tabla.module.css";

/** Valor por el que se ordena una columna. Vacío (`null`, `undefined` o "") va siempre al final. */
export type ValorOrden = string | number | null | undefined;

export interface Columna<F> {
  readonly clave: string;
  /** Encabezado; puede ser solo para lectores de pantalla. */
  readonly titulo: ReactNode;
  /** Rótulo que aparece delante de la celda cuando la tabla se apila (tablet y celular). */
  readonly rotuloMovil?: string;
  readonly derecha?: boolean;
  readonly cifra?: boolean;
  readonly celda: (fila: F) => ReactNode;
  /** Si está, la columna se puede ordenar por este valor (fechas en ISO, montos como número). */
  readonly orden?: (fila: F) => ValorOrden;
  /** Nombre de la columna en el selector de orden de la vista apilada; por defecto, el título si es texto. */
  readonly nombreOrden?: string;
}

/** Textos del orden (vienen de i18n: `t.tabla`). */
export interface TextosOrden {
  readonly ordenarPor: string;
  readonly predeterminado: string;
  readonly ascendente: (columna: string) => string;
  readonly descendente: (columna: string) => string;
}

interface Orden {
  readonly clave: string;
  readonly asc: boolean;
}

const COMPARADOR = new Intl.Collator("es", { numeric: true, sensitivity: "base" });

function vacio(v: ValorOrden): v is null | undefined | "" {
  return v === null || v === undefined || v === "";
}

/** Compara dos valores; los vacíos quedan al final en ambos sentidos. */
export function compararOrden(a: ValorOrden, b: ValorOrden, asc: boolean): number {
  if (vacio(a) || vacio(b)) return vacio(a) === vacio(b) ? 0 : vacio(a) ? 1 : -1;
  const r = typeof a === "number" && typeof b === "number" ? a - b : COMPARADOR.compare(String(a), String(b));
  return asc ? r : -r;
}

/**
 * Tabla de datos. `apilada`: desde 900 px cada fila pasa a tarjeta con rótulos (cinco columnas no caben
 * en una tablet sin partir cada celda). `alNavegar`: toda la fila lleva al detalle, además del enlace
 * de la primera celda (que es el que usa el teclado).
 *
 * Orden: las columnas con `orden` se ordenan con un botón en el encabezado (ascendente, luego descendente);
 * en la vista apilada, donde el encabezado no se ve, con el selector «Ordenar por». Sin elegir, se
 * mantiene el orden en que llegan las filas, que es el que cada pantalla define como natural.
 */
export function Tabla<F>({
  columnas,
  filas,
  clave,
  apilada = true,
  alNavegar,
  pie,
  textosOrden,
}: {
  readonly columnas: readonly Columna<F>[];
  readonly filas: readonly F[];
  readonly clave: (fila: F) => string;
  readonly apilada?: boolean;
  readonly alNavegar?: (fila: F) => void;
  readonly pie?: ReactNode;
  readonly textosOrden?: TextosOrden;
}) {
  const [orden, setOrden] = useState<Orden | null>(null);
  const idSelector = useId();
  const ordenables = textosOrden ? columnas.filter((c) => c.orden) : [];
  const activa = orden ? ordenables.find((c) => c.clave === orden.clave) : undefined;

  const visibles = useMemo(() => {
    const valor = activa?.orden;
    if (!valor || !orden) return filas;
    // Orden estable: a igual valor se respeta el orden de llegada.
    return filas
      .map((f, i) => ({ f, i, v: valor(f) }))
      .sort((a, b) => compararOrden(a.v, b.v, orden.asc) || a.i - b.i)
      .map((x) => x.f);
  }, [filas, activa, orden]);

  const alternar = (c: Columna<F>) => {
    setOrden((o) => (o?.clave === c.clave ? { clave: c.clave, asc: !o.asc } : { clave: c.clave, asc: true }));
  };
  const nombre = (c: Columna<F>) =>
    c.nombreOrden ?? (typeof c.titulo === "string" ? c.titulo : (c.rotuloMovil ?? c.clave));

  return (
    <div className={estilos.envoltura}>
      {apilada && textosOrden && ordenables.length > 0 && (
        <div className={estilos.ordenMovil}>
          <label htmlFor={idSelector}>{textosOrden.ordenarPor}</label>
          <select
            id={idSelector}
            value={orden ? `${orden.clave}:${orden.asc ? "asc" : "desc"}` : ""}
            onChange={(e) => {
              const [c, s] = e.target.value.split(":");
              setOrden(c ? { clave: c, asc: s === "asc" } : null);
            }}
          >
            <option value="">{textosOrden.predeterminado}</option>
            {ordenables.flatMap((c) => [
              <option key={`${c.clave}:asc`} value={`${c.clave}:asc`}>
                {textosOrden.ascendente(nombre(c))}
              </option>,
              <option key={`${c.clave}:desc`} value={`${c.clave}:desc`}>
                {textosOrden.descendente(nombre(c))}
              </option>,
            ])}
          </select>
        </div>
      )}
      <table className={[estilos.tabla, apilada && estilos.apilada].filter(Boolean).join(" ")}>
        <thead>
          <tr>
            {columnas.map((c) => {
              const puede = Boolean(textosOrden && c.orden);
              const sentido = puede && orden?.clave === c.clave ? (orden.asc ? "ascending" : "descending") : null;
              const esta = sentido !== null;
              return (
                <th
                  key={c.clave}
                  scope="col"
                  className={c.derecha ? estilos.derecha : undefined}
                  aria-sort={puede ? (sentido ?? "none") : undefined}
                >
                  {puede ? (
                    <button
                      type="button"
                      className={[estilos.ordenar, esta && estilos.ordenActivo].filter(Boolean).join(" ")}
                      onClick={() => {
                        alternar(c);
                      }}
                    >
                      {c.titulo}
                      <span className={estilos.flecha} aria-hidden="true">
                        {sentido === "ascending" ? "▲" : sentido === "descending" ? "▼" : "↕"}
                      </span>
                    </button>
                  ) : (
                    c.titulo
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {visibles.map((f) => (
            // El clic en la fila es un atajo para el puntero; con teclado se usa el enlace de la primera celda.
            <tr
              key={clave(f)}
              className={alNavegar ? estilos.navegable : undefined}
              onClick={
                alNavegar
                  ? (e) => {
                      // Un clic en un enlace o botón de la fila hace lo suyo.
                      if (!(e.target as HTMLElement).closest("a,button")) alNavegar(f);
                    }
                  : undefined
              }
            >
              {columnas.map((c) => (
                <td
                  key={c.clave}
                  data-rotulo={c.rotuloMovil}
                  className={
                    [c.derecha && estilos.derecha, c.cifra && estilos.cifra].filter(Boolean).join(" ") || undefined
                  }
                >
                  {c.celda(f)}
                </td>
              ))}
            </tr>
          ))}
          {pie}
        </tbody>
      </table>
    </div>
  );
}

/** Fila de totales al final de una tabla. */
export function FilaTotal({ children }: { readonly children: ReactNode }) {
  return <tr className={estilos.total}>{children}</tr>;
}

export const claseCeldaDerecha = estilos.derecha;
